// src/rules.ts
function valuesEqual(left, right) {
  if (Object.is(left, right)) return true;
  if (Array.isArray(left) && Array.isArray(right)) {
    return left.length === right.length && left.every((entry, index) => valuesEqual(entry, right[index]));
  }
  return false;
}
function isEmptyFormValue(value) {
  if (value === void 0 || value === null) return true;
  if (typeof value === "string") return value.trim().length === 0;
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === "object") {
    return Object.values(value).every(
      (entry) => isEmptyFormValue(entry)
    );
  }
  return false;
}
function compareOrderedValues(actual, expected, operator) {
  if (typeof actual === "number" && typeof expected === "number") {
    return operator === "greater_than" ? actual > expected : actual < expected;
  }
  if (typeof actual === "string" && typeof expected === "string") {
    return operator === "greater_than" ? actual > expected : actual < expected;
  }
  return false;
}
function evaluateCondition(actual, operator, expected) {
  switch (operator) {
    case "equals":
      return valuesEqual(actual, expected);
    case "not_equals":
      return !valuesEqual(actual, expected);
    case "is_empty":
      return isEmptyFormValue(actual);
    case "is_not_empty":
      return !isEmptyFormValue(actual);
    case "greater_than":
    case "less_than":
      return compareOrderedValues(actual, expected, operator);
    case "contains":
      if (typeof actual === "string" && typeof expected === "string") {
        return actual.includes(expected);
      }
      if (Array.isArray(actual)) {
        return actual.some((entry) => valuesEqual(entry, expected));
      }
      return false;
  }
}
function evaluateConditionExpression(expression, values) {
  if (expression.kind === "condition") {
    return evaluateCondition(values[expression.fieldId], expression.operator, expression.value);
  }
  if (expression.mode === "all") {
    return expression.conditions.every(
      (condition) => evaluateConditionExpression(condition, values)
    );
  }
  return expression.conditions.some((condition) => evaluateConditionExpression(condition, values));
}
function conditionExpressionForRule(rule) {
  if ("when" in rule) return rule.when;
  return {
    kind: "group",
    mode: rule.conditionMode,
    conditions: rule.conditions.map((condition) => ({
      kind: "condition",
      fieldId: condition.fieldId,
      operator: condition.operator,
      ...condition.value !== void 0 ? { value: condition.value } : {}
    }))
  };
}
function evaluateTemplateRule(rule, values) {
  return evaluateConditionExpression(conditionExpressionForRule(rule), values);
}
function getConditionExpressionFieldIds(expression) {
  if (expression.kind === "condition") return [expression.fieldId];
  return expression.conditions.flatMap((condition) => getConditionExpressionFieldIds(condition));
}
function getRuleConditionFieldIds(rule) {
  return getConditionExpressionFieldIds(conditionExpressionForRule(rule));
}

// src/calculations.ts
function flattenSourceValues(value) {
  return Array.isArray(value) ? value.flatMap((entry) => flattenSourceValues(entry)) : [value];
}
function sourceValues(calculation, values) {
  return calculation.sourceFieldIds.flatMap((fieldId) => flattenSourceValues(values[fieldId]));
}
function numericValue(value) {
  if (typeof value === "number") return Number.isFinite(value) ? value : void 0;
  if (typeof value !== "string") return void 0;
  const trimmed = value.trim();
  if (!/^-?(?:\d+\.?\d*|\.\d+)$/.test(trimmed)) return void 0;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : void 0;
}
function numericOperands(calculation, values) {
  const source = sourceValues(calculation, values);
  if (source.length === 0 || source.some((value) => isEmptyFormValue(value))) {
    return { status: "pending" };
  }
  const numbers = source.map(numericValue);
  if (numbers.some((value) => value === void 0)) {
    return {
      status: "error",
      issue: {
        calculationId: calculation.id,
        targetFieldId: calculation.targetFieldId,
        code: "CALCULATION_SOURCE_INVALID",
        message: `Calculation ${calculation.id} requires numeric source values.`
      }
    };
  }
  return numbers;
}
function timeMinutes(value) {
  if (typeof value !== "string") return void 0;
  const trimmed = value.trim();
  const match = /^(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(trimmed);
  if (match) {
    const hours = Number(match[1]);
    const minutes = Number(match[2]);
    const seconds = Number(match[3] ?? "0");
    if (hours > 23 || minutes > 59 || seconds > 59) return void 0;
    return hours * 60 + minutes + seconds / 60;
  }
  const timestamp = Date.parse(trimmed);
  return Number.isNaN(timestamp) ? void 0 : timestamp / 6e4;
}
function issue(calculation, code, message) {
  return {
    status: "error",
    issue: {
      calculationId: calculation.id,
      targetFieldId: calculation.targetFieldId,
      code,
      message
    }
  };
}
function evaluateCalculation(calculation, values) {
  if (calculation.operator === "duration_minutes") {
    const source = calculation.sourceFieldIds.map((fieldId) => values[fieldId]);
    if (source.some((value) => isEmptyFormValue(value))) return { status: "pending" };
    if (source.length !== 2) {
      return issue(
        calculation,
        "CALCULATION_ARITY_INVALID",
        `Calculation ${calculation.id} requires exactly two duration sources.`
      );
    }
    const start = timeMinutes(source[0]);
    const finish = timeMinutes(source[1]);
    if (start === void 0 || finish === void 0) {
      return issue(
        calculation,
        "CALCULATION_SOURCE_INVALID",
        `Calculation ${calculation.id} requires valid time or date-time source values.`
      );
    }
    return { status: "value", value: finish - start };
  }
  if (calculation.operator === "pass_fail") {
    const source = calculation.sourceFieldIds.map((fieldId) => values[fieldId]);
    if (source.some((value) => isEmptyFormValue(value))) return { status: "pending" };
    if (source.length !== 1 || typeof source[0] !== "boolean") {
      return issue(
        calculation,
        "CALCULATION_SOURCE_INVALID",
        `Calculation ${calculation.id} requires one boolean source value.`
      );
    }
    return { status: "value", value: source[0] ? "Pass" : "Fail" };
  }
  const operands = numericOperands(calculation, values);
  if (!Array.isArray(operands)) return operands;
  switch (calculation.operator) {
    case "sum":
      return { status: "value", value: operands.reduce((total, value) => total + value, 0) };
    case "difference":
      if (operands.length < 2) {
        return issue(
          calculation,
          "CALCULATION_ARITY_INVALID",
          `Calculation ${calculation.id} requires at least two source values.`
        );
      }
      return {
        status: "value",
        value: operands.slice(1).reduce((total, value) => total - value, operands[0] ?? 0)
      };
    case "product":
      return { status: "value", value: operands.reduce((total, value) => total * value, 1) };
    case "percentage": {
      if (operands.length !== 2) {
        return issue(
          calculation,
          "CALCULATION_ARITY_INVALID",
          `Calculation ${calculation.id} requires exactly two source values.`
        );
      }
      const numerator = operands[0] ?? 0;
      const denominator = operands[1] ?? 0;
      if (denominator === 0) {
        if (calculation.divideByZero === "zero") return { status: "value", value: 0 };
        return issue(
          calculation,
          "CALCULATION_DIVIDE_BY_ZERO",
          `Calculation ${calculation.id} cannot divide by zero.`
        );
      }
      return { status: "value", value: numerator / denominator * 100 };
    }
  }
}

// src/repeating.ts
function isRepeatItemAnswers(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const candidate = value;
  return typeof candidate["id"] === "string" && candidate["id"].trim().length > 0 && !!candidate["values"] && typeof candidate["values"] === "object" && !Array.isArray(candidate["values"]);
}
function repeatItemsFromValue(value) {
  return Array.isArray(value) ? value.filter(isRepeatItemAnswers) : [];
}
function repeatingConfiguration(field) {
  return field.type === "repeating_section" ? field.repeating : void 0;
}
function createInitialRepeatItems(field, createItemId) {
  const configuration = repeatingConfiguration(field);
  if (!configuration) return [];
  return Array.from({ length: configuration.minEntries }, () => ({
    id: createItemId(),
    values: {}
  }));
}
function addRepeatItem(field, existing, item) {
  const configuration = repeatingConfiguration(field);
  if (!configuration) return { items: existing, changed: false, reason: "maximum_reached" };
  if (existing.some((entry) => entry.id === item.id)) {
    return { items: existing, changed: false, reason: "duplicate_item_id" };
  }
  if (existing.length >= configuration.maxEntries) {
    return { items: existing, changed: false, reason: "maximum_reached" };
  }
  return { items: [...existing, item], changed: true };
}
function removeRepeatItem(field, existing, itemId) {
  const configuration = repeatingConfiguration(field);
  if (!configuration) return { items: existing, changed: false, reason: "minimum_reached" };
  if (existing.length <= configuration.minEntries) {
    return { items: existing, changed: false, reason: "minimum_reached" };
  }
  const next = existing.filter((entry) => entry.id !== itemId);
  return next.length === existing.length ? { items: existing, changed: false } : { items: next, changed: true };
}

// src/runtime.ts
function repeatingChildMap(definition) {
  const result = /* @__PURE__ */ new Map();
  for (const field of definition.fields) {
    if (field.type !== "repeating_section" || !field.repeating) continue;
    for (const childFieldId of field.repeating.fieldIds) result.set(childFieldId, field.id);
  }
  return result;
}
function gridChildFieldIds(definition) {
  const result = /* @__PURE__ */ new Set();
  for (const field of definition.fields) {
    if (field.type !== "table_grid" || !field.grid) continue;
    for (const column of field.grid.columns) {
      for (const childFieldId of column.fieldIds) result.add(childFieldId);
    }
  }
  return result;
}
function initialValues(definition, answers) {
  const values = { ...answers };
  const repeatChildren = repeatingChildMap(definition);
  for (const field of definition.fields) {
    if (repeatChildren.has(field.id)) continue;
    if (!Object.prototype.hasOwnProperty.call(values, field.id) && field.defaultValue !== void 0) {
      values[field.id] = field.defaultValue;
    }
  }
  return values;
}
function initialItemValues(definition, childFieldIds, answers) {
  const values = { ...answers };
  const childSet = new Set(childFieldIds);
  for (const field of definition.fields) {
    if (childSet.has(field.id) && !Object.prototype.hasOwnProperty.call(values, field.id) && field.defaultValue !== void 0) {
      values[field.id] = field.defaultValue;
    }
  }
  return values;
}
function initialFieldStates(definition, fieldIds) {
  return Object.fromEntries(
    definition.fields.filter((field) => !fieldIds || fieldIds.has(field.id)).map((field) => [
      field.id,
      {
        fieldId: field.id,
        visible: true,
        required: field.required
      }
    ])
  );
}
function valuesEqual2(left, right) {
  if (Object.is(left, right)) return true;
  if (Array.isArray(left) && Array.isArray(right)) {
    return left.length === right.length && left.every((entry, index) => valuesEqual2(entry, right[index]));
  }
  if (left && right && typeof left === "object" && typeof right === "object" && !Array.isArray(left) && !Array.isArray(right)) {
    const leftRecord = left;
    const rightRecord = right;
    const leftKeys = Object.keys(leftRecord);
    const rightKeys = Object.keys(rightRecord);
    return leftKeys.length === rightKeys.length && leftKeys.every(
      (key) => Object.prototype.hasOwnProperty.call(rightRecord, key) && valuesEqual2(leftRecord[key], rightRecord[key])
    );
  }
  return false;
}
function aggregateRepeatValues(definition, values) {
  const aggregates = {};
  for (const field of definition.fields) {
    if (field.type !== "repeating_section" || !field.repeating) continue;
    const items = repeatItemsFromValue(values[field.id]);
    for (const childFieldId of field.repeating.fieldIds) {
      aggregates[childFieldId] = items.map((item) => item.values[childFieldId]).filter((value) => !isEmptyFormValue(value));
    }
  }
  return aggregates;
}
function evaluationValues(definition, values, itemValues) {
  return {
    ...values,
    ...aggregateRepeatValues(definition, values),
    ...itemValues ?? {}
  };
}
function applyDerivedValues(definition, values, targetFieldIds, contextValues, calculationIssues) {
  let changed = false;
  for (const rule of definition.rules) {
    if (rule.effect !== "set_value" || !rule.targetFieldId || !targetFieldIds.has(rule.targetFieldId) || !evaluateTemplateRule(rule, contextValues)) {
      continue;
    }
    if (!valuesEqual2(values[rule.targetFieldId], rule.value)) {
      values[rule.targetFieldId] = rule.value;
      changed = true;
    }
  }
  for (const calculation of definition.calculations) {
    if (!targetFieldIds.has(calculation.targetFieldId)) continue;
    const result = evaluateCalculation(calculation, contextValues);
    if (result.issue) calculationIssues.push(result.issue);
    if (result.status !== "value") continue;
    if (!valuesEqual2(values[calculation.targetFieldId], result.value)) {
      values[calculation.targetFieldId] = result.value;
      changed = true;
    }
  }
  return changed;
}
function evaluateDerivedRuntimeValues(definition, inputValues) {
  const values = { ...inputValues };
  const repeatChildren = repeatingChildMap(definition);
  const topLevelTargets = new Set(
    definition.fields.filter((field) => !repeatChildren.has(field.id)).map((field) => field.id)
  );
  const calculationIssues = [];
  const maximumPasses = definition.rules.length + definition.calculations.length + 2;
  for (let pass = 0; pass < maximumPasses; pass += 1) {
    const before = { ...values };
    const topContext = evaluationValues(definition, values);
    applyDerivedValues(definition, values, topLevelTargets, topContext, calculationIssues);
    for (const field of definition.fields) {
      const repeating = field.repeating;
      if (field.type !== "repeating_section" || !repeating) continue;
      const childIds = new Set(repeating.fieldIds);
      const items = repeatItemsFromValue(values[field.id]);
      const nextItems = items.map((item) => {
        const itemValues = initialItemValues(definition, repeating.fieldIds, item.values);
        const itemPassLimit = definition.rules.length + definition.calculations.length + 2;
        for (let itemPass = 0; itemPass < itemPassLimit; itemPass += 1) {
          const itemBefore = { ...itemValues };
          const context = evaluationValues(definition, values, itemValues);
          applyDerivedValues(definition, itemValues, childIds, context, calculationIssues);
          if (valuesEqual2(itemBefore, itemValues)) break;
        }
        return { id: item.id, values: itemValues };
      });
      values[field.id] = nextItems;
    }
    if (valuesEqual2(before, values)) break;
  }
  const uniqueIssues = /* @__PURE__ */ new Map();
  for (const issue2 of calculationIssues) {
    uniqueIssues.set(`${issue2.calculationId}:${issue2.targetFieldId}:${issue2.code}`, issue2);
  }
  return { values, calculationIssues: [...uniqueIssues.values()] };
}
function applyRuleEffect(rule, fields, warnings, submissionBlocks, messageContext) {
  const targetField = rule.targetFieldId ? fields[rule.targetFieldId] : void 0;
  switch (rule.effect) {
    case "show":
      if (targetField) targetField.visible = true;
      break;
    case "hide":
      if (targetField) targetField.visible = false;
      break;
    case "require":
      if (targetField) targetField.required = true;
      break;
    case "unrequire":
      if (targetField) targetField.required = false;
      break;
    case "display_warning":
      warnings.push({
        ruleId: rule.id,
        message: rule.message ?? "",
        ...messageContext ?? {}
      });
      break;
    case "prevent_submission":
      submissionBlocks.push({
        ruleId: rule.id,
        message: rule.message ?? "",
        ...messageContext ?? {}
      });
      break;
    case "navigate_to_section":
      return rule.targetSectionId;
    case "set_value":
      break;
  }
  return void 0;
}
function ruleRepeatContainer(rule, repeatChildren) {
  if (rule.targetFieldId && repeatChildren.has(rule.targetFieldId)) {
    return repeatChildren.get(rule.targetFieldId);
  }
  const containers = new Set(
    getRuleConditionFieldIds(rule).map((fieldId) => repeatChildren.get(fieldId)).filter((value) => value !== void 0)
  );
  return containers.size === 1 ? [...containers][0] : void 0;
}
function evaluateFormRuntime(definition, answers) {
  const initial = initialValues(definition, answers);
  const derived = evaluateDerivedRuntimeValues(definition, initial);
  const values = derived.values;
  const repeatChildren = repeatingChildMap(definition);
  const topFields = initialFieldStates(definition);
  const warnings = [];
  const submissionBlocks = [];
  let navigationTargetSectionId;
  const topContext = evaluationValues(definition, values);
  for (const rule of definition.rules) {
    if (rule.effect === "set_value") continue;
    if (ruleRepeatContainer(rule, repeatChildren)) continue;
    if (!evaluateTemplateRule(rule, topContext)) continue;
    const navigation = applyRuleEffect(rule, topFields, warnings, submissionBlocks);
    if (navigation) navigationTargetSectionId = navigation;
  }
  const repeats = {};
  for (const field of definition.fields) {
    if (field.type !== "repeating_section" || !field.repeating) continue;
    const childIds = new Set(field.repeating.fieldIds);
    const items = repeatItemsFromValue(values[field.id]);
    const itemStates = [];
    for (const item of items) {
      const fields = initialFieldStates(definition, childIds);
      const itemContext = evaluationValues(definition, values, item.values);
      for (const rule of definition.rules) {
        if (rule.effect === "set_value") continue;
        if (ruleRepeatContainer(rule, repeatChildren) !== field.id) continue;
        if (!evaluateTemplateRule(rule, itemContext)) continue;
        const navigation = applyRuleEffect(rule, fields, warnings, submissionBlocks, {
          repeatFieldId: field.id,
          repeatItemId: item.id
        });
        if (navigation) navigationTargetSectionId = navigation;
      }
      itemStates.push({ itemId: item.id, values: item.values, fields });
    }
    repeats[field.id] = {
      fieldId: field.id,
      minEntries: field.repeating.minEntries,
      maxEntries: field.repeating.maxEntries,
      addAnotherLabel: field.repeating.addAnotherLabel ?? "Add another",
      items: itemStates
    };
  }
  return {
    values,
    fields: topFields,
    repeats,
    warnings,
    submissionBlocks,
    calculationIssues: derived.calculationIssues,
    ...navigationTargetSectionId ? { navigationTargetSectionId } : {}
  };
}
function stringValue(value) {
  return typeof value === "string" ? value.trim() : void 0;
}
function recordValue(value) {
  return value && typeof value === "object" && !Array.isArray(value) ? value : void 0;
}
function validUkPostcode(value) {
  return /^(GIR\s?0AA|(?:[A-Z]{1,2}\d[A-Z\d]?|[A-Z]{1,2}\d{1,2})\s?\d[A-Z]{2})$/i.test(value);
}
function numericValueIsValid(value, integerOnly) {
  if (typeof value === "number") {
    return Number.isFinite(value) && (!integerOnly || Number.isInteger(value));
  }
  if (typeof value !== "string") return false;
  const trimmed = value.trim();
  if (trimmed.length === 0) return false;
  return integerOnly ? /^-?\d+$/.test(trimmed) : /^-?(?:\d+\.?\d*|\.\d+)$/.test(trimmed);
}
function formatIssue(field, value) {
  const text = stringValue(value);
  switch (field.type) {
    case "email":
      if (text !== void 0 && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(text)) {
        return {
          fieldId: field.id,
          code: "EMAIL_INVALID",
          message: `${field.label} must be a valid email address.`
        };
      }
      break;
    case "telephone":
      if (text !== void 0) {
        const digitCount = (text.match(/\d/g) ?? []).length;
        if (!/^\+?[0-9()\s-]+$/.test(text) || digitCount < 7) {
          return {
            fieldId: field.id,
            code: "TELEPHONE_INVALID",
            message: `${field.label} must be a valid telephone number.`
          };
        }
      }
      break;
    case "uk_postcode":
      if (text !== void 0 && !validUkPostcode(text)) {
        return {
          fieldId: field.id,
          code: "UK_POSTCODE_INVALID",
          message: `${field.label} must be a valid UK postcode.`
        };
      }
      break;
    case "address": {
      const address = recordValue(value);
      const postcode = address ? stringValue(address["postcode"]) : void 0;
      if (postcode !== void 0 && !validUkPostcode(postcode)) {
        return {
          fieldId: field.id,
          code: "UK_POSTCODE_INVALID",
          message: `${field.label} must contain a valid UK postcode.`
        };
      }
      break;
    }
    case "what3words":
      if (text !== void 0 && !/^[^.\s]+\.[^.\s]+\.[^.\s]+$/u.test(text)) {
        return {
          fieldId: field.id,
          code: "WHAT3WORDS_INVALID",
          message: `${field.label} must use word.word.word format.`
        };
      }
      break;
    case "integer":
      if (!numericValueIsValid(value, true)) {
        return {
          fieldId: field.id,
          code: "INTEGER_INVALID",
          message: `${field.label} must be a whole number.`
        };
      }
      break;
    case "decimal":
    case "currency":
      if (!numericValueIsValid(value, false)) {
        return {
          fieldId: field.id,
          code: "NUMBER_INVALID",
          message: `${field.label} must be a number.`
        };
      }
      break;
    case "date":
      if (text !== void 0 && !/^\d{4}-\d{2}-\d{2}$/.test(text)) {
        return {
          fieldId: field.id,
          code: "DATE_INVALID",
          message: `${field.label} must be a valid date.`
        };
      }
      break;
    case "time":
      if (text !== void 0 && !/^(?:[01]\d|2[0-3]):[0-5]\d(?::[0-5]\d)?$/.test(text)) {
        return {
          fieldId: field.id,
          code: "TIME_INVALID",
          message: `${field.label} must be a valid time.`
        };
      }
      break;
    case "date_time":
      if (text !== void 0 && Number.isNaN(Date.parse(text))) {
        return {
          fieldId: field.id,
          code: "DATE_TIME_INVALID",
          message: `${field.label} must be a valid date and time.`
        };
      }
      break;
  }
  return void 0;
}
function validateRuntimeFields(definition, runtime, fieldIds) {
  const issues = [];
  const repeatChildren = repeatingChildMap(definition);
  for (const field of definition.fields) {
    if (repeatChildren.has(field.id)) continue;
    if (fieldIds && !fieldIds.has(field.id)) continue;
    const state = runtime.fields[field.id];
    if (!state?.visible) continue;
    const value = runtime.values[field.id];
    if (state.required && isEmptyFormValue(value)) {
      issues.push({
        fieldId: field.id,
        code: "REQUIRED",
        message: `${field.label} is required.`
      });
      continue;
    }
    if (isEmptyFormValue(value)) continue;
    const issue2 = formatIssue(field, value);
    if (issue2) issues.push(issue2);
  }
  return issues;
}
function nestedFieldIds(field) {
  if (field.type === "repeating_section") return field.repeating?.fieldIds ?? [];
  if (field.type === "table_grid") {
    return field.grid?.columns.flatMap((column) => column.fieldIds) ?? [];
  }
  return [];
}
function expandPlacedFieldIds(definition, fieldIds) {
  const fieldById = new Map(definition.fields.map((field) => [field.id, field]));
  const result = /* @__PURE__ */ new Set();
  const visit = (fieldId) => {
    if (result.has(fieldId)) return;
    result.add(fieldId);
    const field = fieldById.get(fieldId);
    if (!field) return;
    for (const childFieldId of nestedFieldIds(field)) visit(childFieldId);
  };
  for (const fieldId of fieldIds) visit(fieldId);
  return [...result];
}
function fieldIdsForPage(definition, pageId) {
  const page = definition.pages.find((candidate) => candidate.id === pageId);
  if (!page) return /* @__PURE__ */ new Set();
  const sectionIds = new Set(page.sectionIds);
  const placed = definition.sections.filter((section) => sectionIds.has(section.id)).flatMap((section) => section.fieldIds);
  return new Set(expandPlacedFieldIds(definition, placed));
}
function validateRepeatsOnPage(definition, runtime, pageFieldIds) {
  const issues = [];
  const fieldById = new Map(definition.fields.map((field) => [field.id, field]));
  for (const field of definition.fields) {
    if (field.type !== "repeating_section" || !field.repeating || !pageFieldIds.has(field.id) || !runtime.fields[field.id]?.visible) {
      continue;
    }
    const repeat = runtime.repeats[field.id];
    const items = repeat?.items ?? [];
    if (items.length < field.repeating.minEntries) {
      issues.push({
        fieldId: field.id,
        code: "REPEAT_MINIMUM",
        message: `${field.label} requires at least ${field.repeating.minEntries} entr${field.repeating.minEntries === 1 ? "y" : "ies"}.`
      });
    }
    if (items.length > field.repeating.maxEntries) {
      issues.push({
        fieldId: field.id,
        code: "REPEAT_MAXIMUM",
        message: `${field.label} allows no more than ${field.repeating.maxEntries} entries.`
      });
    }
    for (const item of items) {
      for (const childFieldId of field.repeating.fieldIds) {
        const child = fieldById.get(childFieldId);
        if (!child) continue;
        const state = item.fields[childFieldId];
        if (!state?.visible) continue;
        const value = item.values[childFieldId];
        if (state.required && isEmptyFormValue(value)) {
          issues.push({
            fieldId: childFieldId,
            repeatFieldId: field.id,
            repeatItemId: item.itemId,
            code: "REQUIRED",
            message: `${child.label} is required.`
          });
          continue;
        }
        if (isEmptyFormValue(value)) continue;
        const issue2 = formatIssue(child, value);
        if (issue2) {
          issues.push({
            ...issue2,
            repeatFieldId: field.id,
            repeatItemId: item.itemId
          });
        }
      }
    }
  }
  return issues;
}
function validateRuntimePage(definition, runtime, pageId) {
  const fieldIds = fieldIdsForPage(definition, pageId);
  return [
    ...validateRuntimeFields(definition, runtime, fieldIds),
    ...validateRepeatsOnPage(definition, runtime, fieldIds)
  ];
}
function directGridChildFieldIds(definition) {
  return gridChildFieldIds(definition);
}

// src/navigation.ts
function formNavigationState(definition, pageId) {
  const requestedIndex = pageId ? definition.pages.findIndex((candidate) => candidate.id === pageId) : 0;
  const pageIndex = requestedIndex >= 0 ? requestedIndex : 0;
  const resolvedPageId = definition.pages[pageIndex]?.id ?? "";
  return {
    pageId: resolvedPageId,
    pageIndex,
    pageCount: definition.pages.length,
    canGoBack: pageIndex > 0,
    canGoForward: pageIndex < definition.pages.length - 1
  };
}
function previousFormPage(definition, currentPageId) {
  const current = formNavigationState(definition, currentPageId);
  const previous = definition.pages[Math.max(0, current.pageIndex - 1)]?.id;
  return formNavigationState(definition, previous);
}
function pageIdForSection(definition, sectionId) {
  return definition.sections.find((section) => section.id === sectionId)?.pageId;
}
function advanceFormPage(definition, runtime, currentPageId) {
  const current = formNavigationState(definition, currentPageId);
  const issues = validateRuntimePage(definition, runtime, current.pageId);
  if (issues.length > 0) return { moved: false, state: current, issues };
  if (runtime.navigationTargetSectionId) {
    const targetPageId = pageIdForSection(definition, runtime.navigationTargetSectionId);
    if (targetPageId) {
      return {
        moved: targetPageId !== current.pageId,
        state: formNavigationState(definition, targetPageId),
        issues: [],
        targetSectionId: runtime.navigationTargetSectionId
      };
    }
  }
  const next = definition.pages[Math.min(definition.pages.length - 1, current.pageIndex + 1)]?.id;
  const state = formNavigationState(definition, next);
  return { moved: state.pageId !== current.pageId, state, issues: [] };
}

// src/index.ts
var phaseBoundary = {
  packageName: "@dispatch/form-engine",
  authorisedPhase: 4,
  status: "active",
  scope: "Shared Phase 4 form runtime, conditional rules, calculations, repeating structures, page navigation and validation."
};
export {
  addRepeatItem,
  advanceFormPage,
  conditionExpressionForRule,
  createInitialRepeatItems,
  directGridChildFieldIds,
  evaluateCalculation,
  evaluateCondition,
  evaluateConditionExpression,
  evaluateFormRuntime,
  evaluateTemplateRule,
  fieldIdsForPage,
  formNavigationState,
  getConditionExpressionFieldIds,
  getRuleConditionFieldIds,
  isEmptyFormValue,
  isRepeatItemAnswers,
  phaseBoundary,
  previousFormPage,
  removeRepeatItem,
  repeatItemsFromValue,
  validateRuntimeFields,
  validateRuntimePage
};
